import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { apiFetch } from "../../api";
import { colors, getClienteSnapshot, getVentaSnapshot, paymentOptionLabels, toNumberOrNull } from "./postventaUtils";

const FORMAS_PAGO = ["CONTADO", "FINANCIACION", "ENTREGA"];

const normalizeForCompare = (data) => (
  JSON.stringify(
    Object.keys(data || {}).sort().reduce((acc, key) => {
      const value = data[key];
      acc[key] = typeof value === "boolean" ? value : String(value ?? "");
      return acc;
    }, {})
  )
);

export default function PostventaFichaDialog({ open, item, onClose, onSaved }) {
  const initialCliente = useMemo(() => getClienteSnapshot(item), [item]);
  const initialVenta = useMemo(() => getVentaSnapshot(item), [item]);
  const [cliente, setCliente] = useState(initialCliente);
  const [venta, setVenta] = useState(initialVenta);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setCliente(initialCliente);
    setVenta(initialVenta);
    setError("");
  }, [open, initialCliente, initialVenta]);

  const hasChanges = useMemo(() => (
    normalizeForCompare(cliente) !== normalizeForCompare(initialCliente)
    || normalizeForCompare(venta) !== normalizeForCompare(initialVenta)
  ), [cliente, initialCliente, venta, initialVenta]);

  if (!item) return null;

  const setClienteField = (field) => (event) => {
    const value = event?.target?.value ?? "";
    setCliente((prev) => ({ ...prev, [field]: value }));
  };

  const setVentaField = (field) => (event) => {
    const value = event?.target?.value ?? "";
    setVenta((prev) => ({ ...prev, [field]: value }));
  };

  const hasFormaPago = (key) => Array.isArray(venta.formas_pago) && venta.formas_pago.includes(key);

  const toggleFormaPago = (key) => {
    setVenta((prev) => {
      const current = Array.isArray(prev.formas_pago) && prev.formas_pago.length ? prev.formas_pago : ["CONTADO"];
      const exists = current.includes(key);
      if (exists && current.length === 1) return prev;
      const next = exists ? current.filter((item) => item !== key) : [...current, key];
      return {
        ...prev,
        formas_pago: next,
        entrega_usado: next.includes("ENTREGA"),
      };
    });
  };

  const handleSave = async () => {
    if (!hasChanges || saving) return;
    setSaving(true);
    setError("");
    try {
      const clientePayload = {
        nombre: cliente.nombre || "",
        telefono: cliente.telefono || null,
        email: cliente.email || null,
        obtenido_de: cliente.obtenido_de || null,
        observacion: cliente.observacion || null,
        fecha_nacimiento: cliente.fecha_nacimiento || null,
        direccion: cliente.direccion || null,
        localidad: cliente.localidad || null,
        codigo_postal: cliente.codigo_postal || null,
        dni: cliente.dni || null,
        cuit_cuil: cliente.cuit_cuil || null,
        estado_civil: cliente.estado_civil || null,
        conyuge_nombre: cliente.conyuge_nombre || null,
        conyuge_dni: cliente.conyuge_dni || null,
        conyuge_fecha_nacimiento: cliente.conyuge_fecha_nacimiento || null,
      };
      const formasPago = Array.isArray(venta.formas_pago) && venta.formas_pago.length ? venta.formas_pago : ["CONTADO"];
      const legacyFormaPago = formasPago.length === 1
        ? (formasPago[0] === "FINANCIACION" ? "FINANCIADO" : formasPago[0])
        : "MIXTO";
      const incluyeEntrega = formasPago.includes("ENTREGA");
      const ventaPayload = {
        estado_vehiculo: venta.estado_vehiculo || null,
        marca: venta.marca || null,
        modelo: venta.modelo || null,
        version: venta.version || null,
        color: venta.color || null,
        kilometraje: venta.kilometraje || null,
        precio_venta: toNumberOrNull(venta.precio_venta),
        moneda_venta: venta.moneda_venta || null,
        forma_pago: legacyFormaPago,
        formas_pago: formasPago,
        monto_efectivo: formasPago.includes("CONTADO") ? toNumberOrNull(venta.monto_efectivo) : null,
        moneda_pago: formasPago.includes("CONTADO") ? venta.moneda_contado || null : null,
        moneda_contado: formasPago.includes("CONTADO") ? venta.moneda_contado || null : null,
        tipo_cambio_contado: formasPago.includes("CONTADO") && venta.moneda_contado === "USD" ? toNumberOrNull(venta.tipo_cambio_contado) : null,
        monto_financiado: formasPago.includes("FINANCIACION") ? toNumberOrNull(venta.monto_financiado) : null,
        tipo_financiacion: formasPago.includes("FINANCIACION") ? venta.tipo_financiacion || null : null,
        financiacion_cuotas: formasPago.includes("FINANCIACION") ? toNumberOrNull(venta.financiacion_cuotas) : null,
        cuota_aproximada: formasPago.includes("FINANCIACION") ? toNumberOrNull(venta.cuota_aproximada) : null,
        moneda_financiacion: formasPago.includes("FINANCIACION") ? venta.moneda_financiacion || null : null,
        tipo_cambio_financiacion: formasPago.includes("FINANCIACION") && venta.moneda_financiacion === "USD" ? toNumberOrNull(venta.tipo_cambio_financiacion) : null,
        entrega_usado: incluyeEntrega,
        usado_marca: incluyeEntrega ? venta.usado_marca || null : null,
        usado_modelo_version: incluyeEntrega ? venta.usado_modelo_version || null : null,
        usado_anio: incluyeEntrega ? venta.usado_anio || null : null,
        usado_kilometraje: incluyeEntrega ? venta.usado_kilometraje || null : null,
        usado_valor_toma: incluyeEntrega ? toNumberOrNull(venta.usado_valor_toma) : null,
        estado_postventa: venta.estado_postventa || "EN_ESPERA",
      };

      const clienteRes = await apiFetch(`/api/clientes/${cliente.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(clientePayload),
      });
      if (!clienteRes.ok) {
        const data = await clienteRes.json().catch(() => ({}));
        throw new Error(data.detail || "No se pudo guardar el cliente.");
      }

      const ventaRes = await apiFetch(`/api/ventas/${item.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(ventaPayload),
      });
      if (!ventaRes.ok) {
        const data = await ventaRes.json().catch(() => ({}));
        throw new Error(data.detail || "No se pudo guardar la venta.");
      }
      await onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e.message || "No se pudieron guardar los cambios.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 900, color: colors.ink }}>Ficha de postventa</Typography>
          <Typography sx={{ fontSize: 12, color: colors.muted }}>
            {item.cliente_nombre} · {item.vehiculo_consultado || `${item.venta?.marca || ""} ${item.venta?.modelo || ""}`}
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={saving}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#fbfdff" }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={1.5}>
          <Accordion sx={{ borderRadius: "10px !important", border: `1px solid ${colors.line}`, boxShadow: "none", "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 900 }}>Cliente</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}><TextField fullWidth label="Nombre" value={cliente.nombre} onChange={setClienteField("nombre")} /></Grid>
                <Grid item xs={12} md={3}><TextField fullWidth label="Teléfono" value={cliente.telefono} onChange={setClienteField("telefono")} /></Grid>
                <Grid item xs={12} md={3}><TextField fullWidth label="Email" value={cliente.email} onChange={setClienteField("email")} /></Grid>
                <Grid item xs={12} md={4}><TextField fullWidth label="Origen" value={cliente.obtenido_de} onChange={setClienteField("obtenido_de")} /></Grid>
                <Grid item xs={12} md={8}><TextField fullWidth label="Observación" value={cliente.observacion} onChange={setClienteField("observacion")} /></Grid>
                <Grid item xs={12} md={3}><TextField fullWidth label="DNI" value={cliente.dni} onChange={setClienteField("dni")} /></Grid>
                <Grid item xs={12} md={3}><TextField fullWidth label="CUIT / CUIL" value={cliente.cuit_cuil} onChange={setClienteField("cuit_cuil")} /></Grid>
                <Grid item xs={12} md={3}><TextField fullWidth label="Fecha nacimiento" type="date" value={cliente.fecha_nacimiento} onChange={setClienteField("fecha_nacimiento")} InputLabelProps={{ shrink: true }} /></Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Estado civil</InputLabel>
                    <Select label="Estado civil" value={cliente.estado_civil || ""} onChange={setClienteField("estado_civil")}>
                      <MenuItem value="Soltero/a">Soltero/a</MenuItem>
                      <MenuItem value="Casado/a">Casado/a</MenuItem>
                      <MenuItem value="Divorciado/a">Divorciado/a</MenuItem>
                      <MenuItem value="Viudo/a">Viudo/a</MenuItem>
                      <MenuItem value="Unión convivencial">Unión convivencial</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={8}><TextField fullWidth label="Dirección" value={cliente.direccion} onChange={setClienteField("direccion")} /></Grid>
                <Grid item xs={12} md={3}><TextField fullWidth label="Localidad" value={cliente.localidad} onChange={setClienteField("localidad")} /></Grid>
                <Grid item xs={12} md={1}><TextField fullWidth label="CP" value={cliente.codigo_postal} onChange={setClienteField("codigo_postal")} /></Grid>
                {(cliente.estado_civil === "Casado/a" || cliente.estado_civil === "Unión convivencial") && (
                  <>
                    <Grid item xs={12} md={5}><TextField fullWidth label="Cónyuge" value={cliente.conyuge_nombre} onChange={setClienteField("conyuge_nombre")} /></Grid>
                    <Grid item xs={12} md={4}><TextField fullWidth label="DNI cónyuge" value={cliente.conyuge_dni} onChange={setClienteField("conyuge_dni")} /></Grid>
                    <Grid item xs={12} md={3}><TextField fullWidth label="Nacimiento cónyuge" type="date" value={cliente.conyuge_fecha_nacimiento} onChange={setClienteField("conyuge_fecha_nacimiento")} InputLabelProps={{ shrink: true }} /></Grid>
                  </>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded sx={{ borderRadius: "10px !important", border: `1px solid ${colors.line}`, boxShadow: "none", "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 900 }}>Vehículo</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Estado vehículo</InputLabel>
                    <Select label="Estado vehículo" value={venta.estado_vehiculo} onChange={setVentaField("estado_vehiculo")}>
                      <MenuItem value="NUEVO">Nuevo</MenuItem>
                      <MenuItem value="USADO">Usado</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}><TextField fullWidth label="Marca" value={venta.marca} onChange={setVentaField("marca")} /></Grid>
                <Grid item xs={12} md={3}><TextField fullWidth label="Modelo" value={venta.modelo} onChange={setVentaField("modelo")} /></Grid>
                <Grid item xs={12} md={3}><TextField fullWidth label="Versión" value={venta.version} onChange={setVentaField("version")} /></Grid>
                <Grid item xs={12} md={3}><TextField fullWidth label="Color" value={venta.color} onChange={setVentaField("color")} /></Grid>
                <Grid item xs={12} md={3}><TextField fullWidth label="Kilometraje" value={venta.kilometraje} onChange={setVentaField("kilometraje")} /></Grid>
                <Grid item xs={12} md={3}><TextField fullWidth label="Precio venta" value={venta.precio_venta} onChange={setVentaField("precio_venta")} /></Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Moneda venta</InputLabel>
                    <Select label="Moneda venta" value={venta.moneda_venta} onChange={setVentaField("moneda_venta")}>
                      <MenuItem value="ARS">ARS</MenuItem>
                      <MenuItem value="USD">USD</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded sx={{ borderRadius: "10px !important", border: `1px solid ${colors.line}`, boxShadow: "none", "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 900 }}>Datos de operación</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  {FORMAS_PAGO.map((key) => (
                    <Button
                      key={key}
                      type="button"
                      variant={hasFormaPago(key) ? "contained" : "outlined"}
                      onClick={() => toggleFormaPago(key)}
                      sx={{ borderRadius: "8px", fontWeight: 800, textTransform: "none", minWidth: 130 }}
                    >
                      {paymentOptionLabels[key]}
                    </Button>
                  ))}
                </Stack>

                {hasFormaPago("CONTADO") && (
                  <Box sx={{ border: "1px solid rgba(0,89,246,0.16)", bgcolor: "rgba(0,89,246,0.03)", borderRadius: 2, p: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6, mb: 1.5, display: "block" }}>
                      Detalle de operación de contado
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}><TextField fullWidth label="Monto" value={venta.monto_efectivo} onChange={setVentaField("monto_efectivo")} /></Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Moneda</InputLabel>
                          <Select label="Moneda" value={venta.moneda_contado} onChange={setVentaField("moneda_contado")}>
                            <MenuItem value="ARS">Pesos</MenuItem>
                            <MenuItem value="USD">USD</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {venta.moneda_contado === "USD" && (
                        <Grid item xs={12} md={4}><TextField fullWidth label="Tipo de cambio" value={venta.tipo_cambio_contado} onChange={setVentaField("tipo_cambio_contado")} /></Grid>
                      )}
                    </Grid>
                  </Box>
                )}

                {hasFormaPago("FINANCIACION") && (
                  <Box sx={{ border: "1px solid rgba(25,118,210,0.16)", bgcolor: "rgba(25,118,210,0.03)", borderRadius: 2, p: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6, mb: 1.5, display: "block" }}>
                      Detalle de financiación
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}><TextField fullWidth label="Monto" value={venta.monto_financiado} onChange={setVentaField("monto_financiado")} /></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth label="Tipo financiación" value={venta.tipo_financiacion} onChange={setVentaField("tipo_financiacion")} /></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth label="Número de cuotas" value={venta.financiacion_cuotas} onChange={setVentaField("financiacion_cuotas")} /></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth label="Valor aprox. cuota" value={venta.cuota_aproximada} onChange={setVentaField("cuota_aproximada")} /></Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Moneda</InputLabel>
                          <Select label="Moneda" value={venta.moneda_financiacion} onChange={setVentaField("moneda_financiacion")}>
                            <MenuItem value="ARS">Pesos</MenuItem>
                            <MenuItem value="USD">USD</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {venta.moneda_financiacion === "USD" && (
                        <Grid item xs={12} md={4}><TextField fullWidth label="Tipo de cambio" value={venta.tipo_cambio_financiacion} onChange={setVentaField("tipo_cambio_financiacion")} /></Grid>
                      )}
                    </Grid>
                  </Box>
                )}

                {hasFormaPago("ENTREGA") && (
                  <Box sx={{ border: "1px solid rgba(76,175,80,0.22)", bgcolor: "rgba(76,175,80,0.04)", borderRadius: 2, p: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6, mb: 1.5, display: "block" }}>
                      Detalle de entrega
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}><TextField fullWidth label="Marca usado" value={venta.usado_marca} onChange={setVentaField("usado_marca")} /></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth label="Modelo y versión" value={venta.usado_modelo_version} onChange={setVentaField("usado_modelo_version")} /></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth label="Año" value={venta.usado_anio} onChange={setVentaField("usado_anio")} /></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth label="Kilometraje" value={venta.usado_kilometraje} onChange={setVentaField("usado_kilometraje")} /></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth label="Valor toma" value={venta.usado_valor_toma} onChange={setVentaField("usado_valor_toma")} /></Grid>
                    </Grid>
                  </Box>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !hasChanges}>
          {saving ? "Guardando..." : "Guardar cambios"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
